import React, { useEffect, useState } from 'react';
import {
  Container,
  Avatar,
  Typography,
  Box,
  Stack,
  TextField,
  Button,
  Divider,
  Card,
  CardContent,
  Chip,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  ChipProps,
  ToggleButton,
  ToggleButtonGroup,
  DialogContent,
  DialogActions,
  useMediaQuery,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  Switch,
  Tooltip,
  ButtonBase,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
  EditOutlined,
  LightModeOutlined,
  DarkModeOutlined,
  PaletteOutlined,
  TranslateOutlined,
  InfoOutlined,
  ContentCopyOutlined,
} from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme as useAppearance } from '../contexts/ThemeContext';
import { PAGE_ACCENTS } from '../theme/pageAccents';
import PageHeader from '../components/common/PageHeader';
import AppDialog, { AppDialogHeader } from '../components/common/AppDialog';
import { apiClient } from '../services/api';
import { PriceEntryMode, UpdateUserRequest, Role, User, ReferralCodeResponse } from '../types';
import { errorMessage } from '../utils/errorMessage';
import { DEFAULT_VAT_RATE, isSupportedVatRate, VAT_RATE_OPTIONS } from '../constants/vat';
import { Link as RouterLink } from 'react-router-dom';
import { getPersonInitials } from '../utils/initials';
import ExportSettings from '../components/profile/ExportSettings';

const normalizeEmail = (email?: string): string => (email || '').trim().toLowerCase();

const Profile: React.FC = () => {
  const { user, updateUser, subscription } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const { mode, setMode } = useAppearance();
  const theme = useMuiTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'), {
    matchMedia: query => window.matchMedia?.(query) ?? {
      matches: false, media: query, onchange: null,
      addListener: () => {}, removeListener: () => {},
      addEventListener: () => {}, removeEventListener: () => {}, dispatchEvent: () => false,
    },
  });
  const canEditProfile = user?.role !== Role.EMPLOYEE;
  const canManagePricing = user?.role === Role.COMPANY_ADMIN;
  const canManageBatchTracking = user?.role === Role.COMPANY_ADMIN;
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [priceEntryMode, setPriceEntryMode] = useState<PriceEntryMode>(
    user?.priceEntryMode || PriceEntryMode.VAT_EXCLUDED,
  );
  const [defaultVatRate, setDefaultVatRate] = useState(String(user?.defaultVatRate ?? DEFAULT_VAT_RATE));
  const [isPricingSaving, setIsPricingSaving] = useState(false);
  const [isPricingDialogOpen, setIsPricingDialogOpen] = useState(false);
  const [batchTrackingEnabled, setBatchTrackingEnabled] = useState(
    user?.batchTrackingEnabled ?? user?.company?.batchTrackingEnabled ?? false,
  );
  const [isBatchTrackingSaving, setIsBatchTrackingSaving] = useState(false);
  const [referral, setReferral] = useState<ReferralCodeResponse | null>(null);
  const [isReferralLoading, setIsReferralLoading] = useState(false);
  const [isReferralCreating, setIsReferralCreating] = useState(false);

  useEffect(() => {
    if (user?.role !== Role.COMPANY_ADMIN) {
      setReferral(null);
      return;
    }
    if (typeof apiClient.getMyReferralCode !== 'function') {
      setReferral(null);
      return;
    }
    setIsReferralLoading(true);
    void apiClient.getMyReferralCode()
      .then(setReferral)
      .catch(() => setReferral(null))
      .finally(() => setIsReferralLoading(false));
  }, [user?.id, user?.role]);
  
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    username: user?.username || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const hideNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handlePricingSettingsSave = async () => {
    if (!user) return;
    const numericRate = Number(defaultVatRate);
    if (!isSupportedVatRate(numericRate)) {
      showNotification(t('profile.defaultVatRateInvalid'), 'error');
      return;
    }
    setIsPricingSaving(true);
    try {
      const updatedCompany = await apiClient.updateCurrentPricingSettings(priceEntryMode, numericRate);
      const savedRate = updatedCompany.defaultVatRate ?? numericRate;
      setDefaultVatRate(String(savedRate));
      updateUser({ ...user, priceEntryMode, defaultVatRate: savedRate });
      showNotification(t('profile.pricingSettingsSaved'));
      setIsPricingDialogOpen(false);
    } catch (error: unknown) {
      setPriceEntryMode(user.priceEntryMode ?? PriceEntryMode.VAT_EXCLUDED);
      setDefaultVatRate(String(user.defaultVatRate ?? DEFAULT_VAT_RATE));
      showNotification(errorMessage(error, t('profile.priceEntryModeSaveError')), 'error');
    } finally {
      setIsPricingSaving(false);
    }
  };

  const priceEntryModeControl = canManagePricing ? (
    <Stack spacing={2}>
      <FormControl fullWidth>
        <InputLabel id="profile-price-entry-mode-label">{t('profile.priceEntryMode')}</InputLabel>
        <Select
          labelId="profile-price-entry-mode-label"
          value={priceEntryMode}
          label={t('profile.priceEntryMode')}
          disabled={isPricingSaving}
          onChange={(event) => setPriceEntryMode(event.target.value as PriceEntryMode)}
        >
          <MenuItem value={PriceEntryMode.VAT_EXCLUDED}>{t('profile.priceEntryModeVatExcluded')}</MenuItem>
          <MenuItem value={PriceEntryMode.VAT_INCLUDED}>{t('profile.priceEntryModeVatIncluded')}</MenuItem>
        </Select>
      </FormControl>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t('profile.priceEntryModeHint')}
      </Typography>
      <FormControl fullWidth error={false} disabled={isPricingSaving}>
        <InputLabel id="profile-default-vat-rate-label">{t('profile.defaultVatRate')}</InputLabel>
        <Select
          labelId="profile-default-vat-rate-label"
          value={defaultVatRate}
          label={t('profile.defaultVatRate')}
          inputProps={{ 'data-testid': 'profile-default-vat-rate' }}
          onChange={(event) => setDefaultVatRate(event.target.value)}
        >
          {VAT_RATE_OPTIONS.map(rate => (
            <MenuItem key={rate} value={String(rate)}>%{rate}</MenuItem>
          ))}
        </Select>
        <FormHelperText>{t('profile.defaultVatRateHint')}</FormHelperText>
      </FormControl>
    </Stack>
  ) : null;

  const handleBatchTrackingSave = async (enabled: boolean) => {
    if (!user) return;
    setBatchTrackingEnabled(enabled);
    setIsBatchTrackingSaving(true);
    try {
      const updatedCompany = await apiClient.updateCurrentBatchTracking(enabled);
      const savedValue = updatedCompany.batchTrackingEnabled ?? enabled;
      setBatchTrackingEnabled(savedValue);
      updateUser({
        ...user,
        batchTrackingEnabled: savedValue,
        company: user.company ? { ...user.company, batchTrackingEnabled: savedValue } : user.company,
      });
      showNotification(t('profile.batchTrackingSaved'));
    } catch (error: unknown) {
      setBatchTrackingEnabled(user.batchTrackingEnabled ?? user.company?.batchTrackingEnabled ?? false);
      const message = errorMessage(error, t('profile.batchTrackingSaveError'));
      showNotification(
        message.includes('batch-tracked items') ? t('profile.batchTrackingDisableError') : message,
        'error',
      );
    } finally {
      setIsBatchTrackingSaving(false);
    }
  };

  const closePricingDialog = () => {
    setPriceEntryMode(user?.priceEntryMode ?? PriceEntryMode.VAT_EXCLUDED);
    setDefaultVatRate(String(user?.defaultVatRate ?? DEFAULT_VAT_RATE));
    setIsPricingDialogOpen(false);
  };

  const companySettings = (canManagePricing || canManageBatchTracking) ? (
    <Stack spacing={2} sx={{ mt: 4 }}>
      {canManagePricing && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <ButtonBase onClick={() => setIsPricingDialogOpen(true)} sx={{ fontSize: '1rem', fontWeight: 600, textAlign: 'left', borderRadius: 0.5 }}>
            {t('profile.pricingSettings')}
          </ButtonBase>
          <Tooltip title={t('profile.pricingSettingsHint')} arrow>
            <IconButton size="small" aria-label={t('profile.pricingSettingsHint')}><InfoOutlined fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
        <IconButton onClick={() => setIsPricingDialogOpen(true)} aria-label={t('profile.editPricingSettings')}>
          <EditOutlined fontSize="small" />
        </IconButton>
      </Box>}
      {canManageBatchTracking && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography component="label" htmlFor="profile-batch-tracking" sx={{ cursor: 'pointer', fontWeight: 600 }}>{t('profile.batchTrackingSetting')}</Typography>
          <Tooltip title={t('profile.batchTrackingHint')} arrow>
            <IconButton size="small" aria-label={t('profile.batchTrackingHint')}><InfoOutlined fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
        <Switch
          id="profile-batch-tracking"
          checked={batchTrackingEnabled}
          onChange={(event) => { void handleBatchTrackingSave(event.target.checked); }}
          disabled={isBatchTrackingSaving}
        />
      </Box>}
    </Stack>
  ) : null;

  const handleCreateReferralCode = async () => {
    setIsReferralCreating(true);
    try {
      setReferral(await apiClient.createMyReferralCode());
      showNotification(t('profile.referralCodeCreated'));
    } catch (error: unknown) {
      showNotification(errorMessage(error, t('profile.referralCodeError')), 'error');
    } finally {
      setIsReferralCreating(false);
    }
  };

  const copyReferralValue = async (value: string, message: string) => {
    try {
      await navigator.clipboard.writeText(value);
      showNotification(message);
    } catch {
      showNotification(t('profile.referralCodeCopyError'), 'error');
    }
  };

  const referralCard = user?.role === Role.COMPANY_ADMIN ? (
    <Box component="section" aria-label={t('profile.referralCode')} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper' }}>
      <Typography component="h2" variant="subtitle1" fontWeight={750}>{t('profile.referralCode')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t('profile.referralCodeHint')}</Typography>
      {isReferralLoading ? <Typography variant="body2" sx={{ mt: 1.5 }}>{t('common.loading')}</Typography> : referral?.code ? <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
          <Typography variant="h6" fontFamily="monospace" letterSpacing={1.5}>{referral.code}</Typography>
          <IconButton size="small" aria-label={t('profile.copyReferralCode')} onClick={() => void copyReferralValue(referral.code!, t('profile.referralCodeCopied'))} disabled={!referral.active}>
            <ContentCopyOutlined fontSize="small" />
          </IconButton>
        </Box>
        {referral.active && referral.shareUrl && <Button size="small" startIcon={<ContentCopyOutlined />} onClick={() => void copyReferralValue(referral.shareUrl!, t('profile.referralLinkCopied'))} sx={{ mt: 0.75 }}>
          {t('profile.copyReferralLink')}
        </Button>}
        {!referral.active && <Alert severity="warning" sx={{ mt: 1 }}>{t('profile.referralCodeInactive')}</Alert>}
      </> : <Button variant="outlined" size="small" onClick={() => void handleCreateReferralCode()} disabled={isReferralCreating} sx={{ mt: 1.5 }}>
        {isReferralCreating ? t('common.loading') : t('profile.createReferralCode')}
      </Button>}
    </Box>
  ) : null;

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('profile.firstNameRequired');
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t('profile.lastNameRequired');
    }

    if (!formData.username.trim()) {
      newErrors.username = t('profile.usernameRequired');
    } else if (formData.username.length < 3) {
      newErrors.username = t('profile.usernameMinLength');
    } else if (formData.username.length > 50) {
      newErrors.username = t('profile.usernameMaxLength');
    }

    const normalizedEmail = normalizeEmail(formData.email);
    if (!normalizedEmail) {
      newErrors.email = t('profile.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      newErrors.email = t('profile.emailInvalid');
    }

    const emailChanged = normalizedEmail !== normalizeEmail(user?.email);
    if (normalizedEmail && emailChanged && !formData.currentPassword) {
      newErrors.currentPassword = t('profile.currentPasswordRequiredForEmail');
    }

    // Password validation applies only when a new password is being entered.
    // The current password may also be entered solely to authorize an email change.
    if (formData.newPassword || formData.confirmPassword) {
      if (!formData.currentPassword) {
        newErrors.currentPassword = t('profile.currentPasswordRequiredForPassword');
      }
      if (!formData.newPassword) {
        newErrors.newPassword = t('profile.newPasswordRequired');
      } else if (formData.newPassword.length < 6) {
        newErrors.newPassword = t('profile.newPasswordMinLength');
      }
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = t('profile.passwordsMismatch');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !user) return;

    try {
      setIsLoading(true);

      const emailChanged = normalizeEmail(formData.email) !== normalizeEmail(user.email);
      let updatedUser: User;

      if (emailChanged) {
        // Email ownership is handled by its dedicated verification flow.
        await apiClient.requestEmailChange(user.id, {
          newEmail: formData.email.trim(),
          currentPassword: formData.currentPassword,
        });
      }

      const updateData: UpdateUserRequest = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
      };

      // Add password if user wants to change it
      if (formData.newPassword) {
        updateData.password = formData.newPassword;
      }

      updatedUser = await apiClient.updateUser(user.id, updateData);
      updateUser(updatedUser);
      
      // Clear password fields
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));

      setIsEditing(false);
      showNotification(emailChanged ? t('profile.emailVerificationSent') : t('profile.updateSuccess'), emailChanged ? 'info' : 'success');
    } catch (error: unknown) {
      showNotification(errorMessage(error, t('profile.updateError')), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      username: user?.username || '',
      email: user?.email || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setErrors({});
    setIsEditing(false);
  };

  const getRoleColor = (role: Role): ChipProps['color'] => {
    switch (role) {
      case Role.SUPER_ADMIN: return 'error';
      case Role.COMPANY_ADMIN: return 'warning';
      case Role.EMPLOYEE: return 'info';
      default: return 'default';
    }
  };

  const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.username || '';
  const initials = getPersonInitials(user?.firstName, user?.lastName, language, user?.username) || '?';

  const subscriptionSummary = user?.role !== Role.SUPER_ADMIN && subscription ? (
    <Alert severity={subscription.readOnly ? 'warning' : (subscription.remainingDays !== null && subscription.remainingDays !== undefined && subscription.remainingDays <= 30 ? 'info' : 'success')}>
      <Typography variant="subtitle2" fontWeight={700}>
        {language === 'tr' ? 'Abonelik durumu' : 'Subscription status'}
      </Typography>
      <Typography variant="body2">
        {subscription.readOnly
          ? (language === 'tr' ? 'Deneme veya abonelik süreniz sona erdi. Hesabınız salt okunur durumda.' : 'Your trial or subscription has ended. Your account is read-only.')
          : subscription.remainingDays !== null && subscription.remainingDays !== undefined
            ? (language === 'tr' ? `${subscription.remainingDays} gün kullanım süreniz kaldı.` : `${subscription.remainingDays} days remaining.`)
            : (language === 'tr' ? 'Aboneliğiniz aktif.' : 'Your subscription is active.')}
      </Typography>
      {subscription.endsAt && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
        {language === 'tr' ? 'Bitiş: ' : 'Ends: '}{new Date(subscription.endsAt).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US')}
      </Typography>}
    </Alert>
  ) : null;

  if (!user) {
    return (
      <Container maxWidth={false} disableGutters sx={{ pt: 0, pb: 4 }}>
        <Alert severity="error">{t('profile.loadError')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ pt: 0, pb: { xs: 2, sm: 6 } }}>
      {isMobileLayout ? <Box sx={{ bgcolor: 'background.paper', minHeight: 'calc(100dvh - 60px)', px: 1.5, py: 1.5 }}>
        <Stack spacing={1.5}>
        <Box component="section" aria-label={fullName} sx={{ display: 'flex', gap: 1.75, alignItems: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 2.5, bgcolor: 'action.hover' }}>
          <Avatar sx={{ width: 54, height: 54, bgcolor: 'action.selected', color: 'primary.main', fontWeight: 750, fontSize: 19 }}>{initials}</Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={750} sx={{ lineHeight: 1.25, overflowWrap: 'anywhere' }}>{fullName}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>@{user.username}</Typography>
            <Chip label={t(`role.${user.role}`)} color={getRoleColor(user.role)} size="small" sx={{ mt: 0.75, fontWeight: 700 }} />
          </Box>
        </Box>

        <Box component="section" aria-label={t('profile.companyInfo')} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper' }}>
          <Typography component="h2" variant="subtitle1" fontWeight={750} sx={{ mb: 1.5 }}>{t('profile.companyInfo')}</Typography>
          <Typography fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{user.companyName || user.company?.name || t('profile.noCompany')}</Typography>
          {companySettings}
        </Box>

        {referralCard}

        <Box component="section" aria-label={t('profile.personalInfo')} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
            <Typography component="h2" variant="subtitle1" fontWeight={750}>{t('profile.personalInfo')}</Typography>
            {canEditProfile && <Button size="small" startIcon={<EditOutlined />} onClick={() => setIsEditing(true)} sx={{ minHeight: 44 }}>{t('profile.edit')}</Button>}
          </Box>
          {user.pendingEmail && <Alert severity="warning" sx={{ mb: 1.5 }}>
            {t('profile.emailVerificationPending')}: <strong>{user.pendingEmail}</strong>
            <Button component={RouterLink} to={`/verify-email?email=${encodeURIComponent(user.pendingEmail)}`} size="small">{t('profile.verifyEmail')}</Button>
          </Alert>}
          {[
            [t('profile.firstName'), user.firstName],
            [t('profile.lastName'), user.lastName],
            [t('profile.username'), user.username],
            [t('profile.email'), user.email],
          ].map(([label, value]) => <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
            <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', overflowWrap: 'anywhere', minWidth: 0 }}>{value || '—'}</Typography>
          </Box>)}
          {!canEditProfile && <Alert severity="info" sx={{ mt: 1 }}>{t('profile.managedByCompanyAdmin')}</Alert>}
        </Box>

        {subscriptionSummary}

        <Box component="section" aria-label={t('profile.preferences')} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper' }}>
          <Typography component="h2" variant="subtitle1" fontWeight={750} sx={{ mb: 2 }}>{t('profile.preferences')}</Typography>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('profile.appearance')}</Typography>
          <ToggleButtonGroup exclusive value={mode} onChange={(_, value) => { if (value) setMode(value); }} fullWidth aria-label={t('profile.appearance')} sx={{ mb: 2.5, '& .MuiToggleButton-root': { minHeight: 44, textTransform: 'none' } }}>
            <ToggleButton value="light" aria-label={t('theme.light')}><LightModeOutlined fontSize="small" sx={{ mr: 0.75 }} />{t('theme.light')}</ToggleButton>
            <ToggleButton value="dark" aria-label={t('theme.dark')}><DarkModeOutlined fontSize="small" sx={{ mr: 0.75 }} />{t('theme.dark')}</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('profile.language')}</Typography>
          <ToggleButtonGroup exclusive value={language} onChange={(_, value) => { if (value) setLanguage(value); }} fullWidth aria-label={t('profile.language')} sx={{ '& .MuiToggleButton-root': { minHeight: 44, textTransform: 'none' } }}>
            <ToggleButton value="tr">Türkçe</ToggleButton>
            <ToggleButton value="en">English</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {canManagePricing && <ExportSettings />}
        </Stack>
      </Box> : <>
      <PageHeader
        title={fullName}
        icon={<Box component="span" sx={{ fontSize: 20, fontWeight: 700 }}>
          {(user.firstName?.[0] || user.username?.[0] || '?').toLocaleUpperCase(language)}{user.lastName?.[0]?.toLocaleUpperCase(language) || ''}
        </Box>}
        accent="profile"
        description={<Typography color="text.secondary" variant="body2">@{user.username}</Typography>}
        actions={<Chip label={t(`role.${user.role}`)} color={getRoleColor(user.role)} sx={{ fontWeight: 700 }} />}
      />

      <Stack spacing={3}>
        {subscriptionSummary}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 3 }}>
        {/* Company Information */}
        <Card sx={{ minWidth: 0, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, '&:last-child': { pb: { xs: 2.5, md: 3.5 } } }}>
            <Typography variant="h6" display="flex" alignItems="center" gap={1.25} mb={2} fontWeight={700}>
              <BusinessIcon color="primary" />
              {t('profile.companyInfo')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: 'action.hover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
              <BusinessIcon color="action" sx={{ flexShrink: 0 }} />
              <Typography fontWeight={700} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{user.companyName || user.company?.name || t('profile.noCompany')}</Typography>
            </Box>
            {companySettings}
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 0, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, '&:last-child': { pb: { xs: 2.5, md: 3.5 } } }}>
            <Typography variant="h6" display="flex" alignItems="center" gap={1.25} fontWeight={700}>
              <PaletteOutlined color="primary" />
              {t('profile.preferences')}
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5, mb: 2.5 }}>{t('profile.preferencesHint')}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 3 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('profile.appearance')}</Typography>
                <ToggleButtonGroup exclusive value={mode} onChange={(_, value) => { if (value) setMode(value); }} size="small" fullWidth aria-label={t('profile.appearance')}>
                  <ToggleButton value="light" aria-label={t('theme.light')}><LightModeOutlined fontSize="small" sx={{ mr: 0.75 }} />{t('theme.light')}</ToggleButton>
                  <ToggleButton value="dark" aria-label={t('theme.dark')}><DarkModeOutlined fontSize="small" sx={{ mr: 0.75 }} />{t('theme.dark')}</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box>
                <Typography variant="subtitle2" display="flex" alignItems="center" gap={0.75} sx={{ mb: 1 }}><TranslateOutlined fontSize="small" />{t('profile.language')}</Typography>
                <ToggleButtonGroup exclusive value={language} onChange={(_, value) => { if (value) setLanguage(value); }} size="small" fullWidth aria-label={t('profile.language')}>
                  <ToggleButton value="tr">Türkçe</ToggleButton>
                  <ToggleButton value="en">English</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          </CardContent>
        </Card>
        </Box>

        {referralCard}

        {/* User Information */}
        <Card sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, '&:last-child': { pb: { xs: 2.5, md: 3.5 } } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" display="flex" alignItems="center" gap={1.25} fontWeight={700}>
                <PersonIcon sx={{ color: PAGE_ACCENTS.profile[theme.palette.mode] }} />
                {t('profile.personalInfo')}
              </Typography>
              {!isEditing && canEditProfile && (
                <Button variant="outlined" startIcon={<EditOutlined />} onClick={() => setIsEditing(true)}>
                  {t('profile.edit')}
                </Button>
              )}
            </Box>

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5
            }}>
              {user.pendingEmail && (
                <Alert severity="warning" sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                  {t('profile.emailVerificationPending')}: <strong>{user.pendingEmail}</strong>
                  <Button
                    component={RouterLink}
                    to={`/verify-email?email=${encodeURIComponent(user.pendingEmail)}`}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    {t('profile.verifyEmail')}
                  </Button>
                </Alert>
              )}
              <TextField
                fullWidth
                label={t('profile.firstName')}
                value={formData.firstName}
                onChange={handleInputChange('firstName')}
                error={!!errors.firstName}
                helperText={errors.firstName}
                disabled={!isEditing || isLoading}
                required
              />
              <TextField
                fullWidth
                label={t('profile.lastName')}
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                error={!!errors.lastName}
                helperText={errors.lastName}
                disabled={!isEditing || isLoading}
                required
              />
              <TextField
                fullWidth
                label={t('profile.username')}
                value={formData.username}
                onChange={handleInputChange('username')}
                error={!!errors.username}
                helperText={errors.username}
                disabled={!isEditing || isLoading}
                required
              />
              <TextField
                fullWidth
                label={t('profile.email')}
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                disabled={!isEditing || isLoading}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'action.hover' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('profile.role')}
                </Typography>
                <Chip
                  label={t(`role.${user.role}`) || user.role?.replace('_', ' ')}
                  color={getRoleColor(user.role)}
                  size="medium"
                />
              </Box>
            </Box>

            {!canEditProfile && (
              <Alert severity="info" sx={{ mt: 3 }}>
                {t('profile.managedByCompanyAdmin')}
              </Alert>
            )}

            {isEditing && canEditProfile && (
              <>
                <Divider sx={{ my: 3 }} />
                
                <Typography variant="h6" display="flex" alignItems="center" gap={1.25} mb={2} fontWeight={700}>
                  <SecurityIcon color="primary" />
                  {t('profile.changePassword')}
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    label={t('profile.currentPassword')}
                    type={showPassword ? 'text' : 'password'}
                    value={formData.currentPassword}
                    onChange={handleInputChange('currentPassword')}
                    error={!!errors.currentPassword}
                    helperText={errors.currentPassword || (normalizeEmail(formData.email) && normalizeEmail(formData.email) !== normalizeEmail(user.email)
                      ? t('profile.currentPasswordRequiredForEmail')
                      : undefined)}
                    disabled={isLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? t('profile.hidePassword') : t('profile.showPassword')}
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 3 
                  }}>
                    <TextField
                      fullWidth
                      label={t('profile.newPassword')}
                      type={showPassword ? 'text' : 'password'}
                      value={formData.newPassword}
                      onChange={handleInputChange('newPassword')}
                      error={!!errors.newPassword}
                      helperText={errors.newPassword}
                      disabled={isLoading}
                    />
                    <TextField
                      fullWidth
                      label={t('profile.confirmPassword')}
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleInputChange('confirmPassword')}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword}
                      disabled={isLoading}
                    />
                  </Box>
                </Box>

                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={1.5} mt={3}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={isLoading}
                    startIcon={<SaveIcon />}
                  >
                    {t('profile.saveChanges')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={isLoading}
                  >
                    {t('profile.cancel')}
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        {canManagePricing && <ExportSettings />}
      </Stack>
      </>}

      {canManagePricing && <AppDialog presentation="form" open={isPricingDialogOpen} onClose={isPricingSaving ? undefined : closePricingDialog} closeDisabled={isPricingSaving} fullWidth maxWidth="sm">
        <AppDialogHeader icon={<BusinessIcon />}>{t('profile.pricingSettings')}</AppDialogHeader>
        <DialogContent>{priceEntryModeControl}</DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closePricingDialog} disabled={isPricingSaving}>{t('profile.cancel')}</Button>
          <Button data-testid="profile-pricing-save" variant="contained" startIcon={<SaveIcon />} onClick={() => { void handlePricingSettingsSave(); }} disabled={isPricingSaving}>{t('common.save')}</Button>
        </DialogActions>
      </AppDialog>}

      {isMobileLayout && canEditProfile && <AppDialog presentation="form" open={isEditing} onClose={isLoading ? undefined : handleCancel} closeDisabled={isLoading} fullWidth maxWidth="sm">
        <AppDialogHeader icon={<PersonIcon />} subtitle={fullName}>{t('profile.editProfile')}</AppDialogHeader>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pb: 3 }}>
            <TextField fullWidth label={t('profile.firstName')} value={formData.firstName} onChange={handleInputChange('firstName')} error={!!errors.firstName} helperText={errors.firstName} disabled={isLoading} required />
            <TextField fullWidth label={t('profile.lastName')} value={formData.lastName} onChange={handleInputChange('lastName')} error={!!errors.lastName} helperText={errors.lastName} disabled={isLoading} required />
            <TextField fullWidth label={t('profile.username')} value={formData.username} onChange={handleInputChange('username')} error={!!errors.username} helperText={errors.username} disabled={isLoading} required />
            <TextField fullWidth label={t('profile.email')} type="email" value={formData.email} onChange={handleInputChange('email')} error={!!errors.email} helperText={errors.email} disabled={isLoading} required />
            <Divider />
            <Typography variant="subtitle1" fontWeight={700}>{t('profile.changePassword')}</Typography>
            <TextField fullWidth label={t('profile.currentPassword')} type={showPassword ? 'text' : 'password'} value={formData.currentPassword} onChange={handleInputChange('currentPassword')} error={!!errors.currentPassword} helperText={errors.currentPassword || (normalizeEmail(formData.email) && normalizeEmail(formData.email) !== normalizeEmail(user.email) ? t('profile.currentPasswordRequiredForEmail') : undefined)} disabled={isLoading} InputProps={{ endAdornment: <InputAdornment position="end"><IconButton aria-label={showPassword ? t('profile.hidePassword') : t('profile.showPassword')} onClick={() => setShowPassword(!showPassword)} edge="end">{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
            <TextField fullWidth label={t('profile.newPassword')} type={showPassword ? 'text' : 'password'} value={formData.newPassword} onChange={handleInputChange('newPassword')} error={!!errors.newPassword} helperText={errors.newPassword} disabled={isLoading} />
            <TextField fullWidth label={t('profile.confirmPassword')} type={showPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={handleInputChange('confirmPassword')} error={!!errors.confirmPassword} helperText={errors.confirmPassword} disabled={isLoading} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCancel} disabled={isLoading}>{t('profile.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={isLoading} startIcon={<SaveIcon />}>{t('profile.saveChanges')}</Button>
        </DialogActions>
      </AppDialog>}

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={hideNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Profile;
